import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Params} from '@angular/router';
import {Store} from '@ngxs/store';
import {NzMarks} from 'ng-zorro-antd/slider';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {IRecordEmit} from '../../interfaces/record';
import {AuthState, IUser} from '../../models/auth';
import {Loader} from '../../models/loader/loader.redux';
import {IRecordHistoryAttitude, RecordHistoryAttitudeAction, RecordHistoryAttitudeState} from '../../models/record-history-attitude';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {StorageService} from '../../services/storage/storage.service';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {cloneDeep} from 'lodash';
import {ServiceAction, ServiceState} from '../../models/service';
import {Logger} from '../../class/helper/logger.class';
import {ENRICHING_ACTIVITIES} from '../../class/data';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-attitude',
  templateUrl: './record-history-attitude.component.html',
  styleUrls: ['./record-history-attitude.component.scss'],
  animations: [fadeInAnimation]

})
export class RecordHistoryAttitudeComponent implements OnInit, OnDestroy {

  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(RecordHistoryAttitudeState.getWasDeployed);

  @Input() recordHistoryAttitude: IRecordHistoryAttitude;
  @Output() recordHistoryAttitudeChange = new EventEmitter<IRecordEmit>();

  form: UntypedFormGroup;

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;

  userLogged: IUser;
  disableForm: boolean;
  loadInfo: boolean;

  wasDeployedSubscription: Subscription;

  marks: NzMarks = {
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
    6: '',
    7: '',
    8: '',
    9: '',
    10: '',
    11: '',
    12: '',
    13: '',
    14: '',
    15: '',
  };

  skeletonTemplate: any[] = [];

  // EnrichingActivities
  enrichingActivities = ENRICHING_ACTIVITIES;
  activeObservables: boolean;

  constructor(
    private store: Store,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private messageAlert: MessageAlertService,
    private skeletonService: SkeletonService,
  ) {
    this.disableForm = false;
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordHistoryAttitude) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnInit(): void {
    this.initValues();
  }

  initValues(): void {
    this.activeObservables = true;
    this.form = new UntypedFormGroup({});
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS)
  }

  async initValuesHttp(): Promise<void> {
    try {
      // await firstValueFrom(this.store.dispatch(new Loader(true, 'Cargando afectividad de entornos ...')));
      this.getParams();
      this.userLogged = cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordHistoryAttitudeAction.GetActive(this.serviceId, this.patientId)));
      this.recordHistoryAttitude = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordHistoryAttitudeState.getRecordHistoryAttitude)));
      this.recordHistoryAttitudeChange.emit({ record: this.recordHistoryAttitude, updated: false });

      this.setFormProperties(this.recordHistoryAttitude);
      this.loadInfo = true;
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  setFormProperties(recordHistoryAttitude: IRecordHistoryAttitude): void {
    this.disableForm = !this.userLogged?.experts;

    this.form = new UntypedFormGroup({
      // Add types
      physicallyHealthy: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.physicallyHealthy : 0, []),
      wellPhysicalHealthControl: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.wellPhysicalHealthControl : 0, []),
      satisfiedWithMyWeight: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.satisfiedWithMyWeight : 0, []),
      enoughEnergyToDoMyThings: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.enoughEnergyToDoMyThings : 0, []),
      takeCareMyPhysicalHealth: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.takeCareMyPhysicalHealth : 0, []),
      planMyFuture: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.planMyFuture : 0, []),
      ableToSolveProblems: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.ableToSolveProblems : 0, []),
      satisfiedWithMyPhysicalAppearance: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.satisfiedWithMyPhysicalAppearance : 0, []),
      potentialToReachMyGoals: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.potentialToReachMyGoals : 0, []),
      makeADifferenceInOtherLives: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.makeADifferenceInOtherLives : 0, []),
      engageStimulatingConversations: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.engageStimulatingConversations : 0, []),
      lifesMakesSense: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.lifesMakesSense : 0, []),
      satisfiedSpiritually: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.satisfiedSpiritually : 0, []),
      smartThanOthers: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.smartThanOthers : 0, []),
      bringOutMyCreativeSide: new UntypedFormControl(recordHistoryAttitude ? recordHistoryAttitude.bringOutMyCreativeSide : 0, []),
      // use types for reactive forms
      hasEnrichingActivity: new FormControl<boolean>(recordHistoryAttitude.hasEnrichingActivity ?? false),
      enrichingActivity: new FormControl<string>(recordHistoryAttitude.enrichingActivity ?? ENRICHING_ACTIVITIES[0]),
      enrichingActivityFrequency: new FormControl<number>(recordHistoryAttitude.enrichingActivityFrequency ?? 0),
    });
    if (recordHistoryAttitude.enrichingActivity && !this.enrichingActivities.includes(recordHistoryAttitude.enrichingActivity)) {
      this.enrichingActivities.push(recordHistoryAttitude.enrichingActivity);
    }
    if (!this.userLogged.experts) {
      this.form.disable();
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  emitChangeUpdate(): void {
    this.recordHistoryAttitude.updatedAt = new Date().toISOString();
    this.recordHistoryAttitudeChange.emit({ record: this.recordHistoryAttitude, updated: true });
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando historial ...')));
      await firstValueFrom(this.store.dispatch(new RecordHistoryAttitudeAction.PatchActive(
        this.serviceId, this.patientId, this.form.getRawValue())
      ));
      this.messageAlert.createMessage('success', 'Historial de actitud guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar el historial de actitud. Intente más tarde', 5000);

      this.recordHistoryAttitude = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordHistoryAttitudeState.getRecordHistoryAttitude)));
      this.setFormProperties(this.recordHistoryAttitude);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  sliderFormatter(value: number): string {
    let status = '';
    switch (value) {
      case 1:
        status = 'fuertemente en desacuerdo';
        break;
      case 2:
        status = 'moderadamente en desacuerdo';
        break;
      case 3:
        status = 'Medianamente en desacuerdo';
        break;
      case 4:
        status = 'Medianamente de acuerdo';
        break;
      case 5:
        status = 'Moderadamente de acuerdo';
        break;
      case 6:
        status = 'Fuertemente de acuerdo';
        break;
    }
    return status;
  }

  onAddOtherNutritionalRegimen(input: HTMLInputElement): void {
    const value = input.value;
    if (!value) return;
    if (this.enrichingActivities.filter(regimen => regimen === value)) {
      this.enrichingActivities.pop();
    }
    this.enrichingActivities = [...this.enrichingActivities, value];
  }

  ngOnDestroy(): void {
    this.activeObservables = false;
    this.wasDeployedSubscription.unsubscribe();
  }
}
